import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import { Unauthorized } from '@virtual-care/client';
import { Toaster, TooltipProvider } from '@virtual-care/shadcn';
import { client, errorToFormIssue } from '@virtual-care/ui';

import Layout from './app/layout.tsx';
import Home from './app/routes/home.tsx';
import OurDoctors from './app/routes/our-doctors.tsx';
import TermsAndPrivacy from './app/routes/terms-and-privacy.tsx';

const router = createBrowserRouter([
  {
    path: '/',
    Component: Layout,
    children: [
      {
        index: true,
        Component: Home,
      },
      {
        path: 'our-doctors',
        Component: OurDoctors,
      },
    ],
  },
  {
    path: 'terms-and-privacy',
    Component: TermsAndPrivacy,
  },
]);

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error(error);
      if (error instanceof Unauthorized) {
        if (error.status === 401) {
          toast.error('You are not authorized to perform this action');
          window.location.href = '/';
        }
      } else if (import.meta.env.DEV) {
        const formIssue = errorToFormIssue(error);
        for (const propName in formIssue) {
          toast.error(
            `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
          );
        }
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
      const formIssue = errorToFormIssue(error);
      for (const propName in formIssue) {
        if (import.meta.env.DEV) {
          toast.error(
            `Error in propName "${propName}": ${formIssue[propName].map((issue) => issue.message).join(', ')}`,
          );
        }
      }
    },
  }),
});

client.setOptions({
  baseUrl: import.meta.env.VITE_API_URL,
  token: () => '',
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Toaster />
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </TooltipProvider>
  </StrictMode>,
);
